import React, { useMemo } from 'react';
import { DatePicker, Radio, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

const INHUB_MAX_SPAN_DAYS = 30;

export type TimeRangePreset = 'LAST_24H' | 'LAST_48H' | 'CUSTOM';

export interface TimeRangeValue {
    preset: TimeRangePreset;
    customRange?: [Dayjs, Dayjs];
}

interface Props {
    value: TimeRangeValue;
    onChange: (next: TimeRangeValue) => void;
}

const presetToHours = (preset: TimeRangePreset): number => {
    if (preset === 'LAST_48H') return 48;
    return 24;
};

const TimeRangeFilter: React.FC<Props> = ({ value, onChange }) => {
    const { t } = useTranslation();
    const I18N = 'wms.delivery.inhubDashboard.timeRange';

    const handlePresetChange = (newPreset: TimeRangePreset) => {
        // 切到 CUSTOM：每次都按"切走前的 preset 窗口"重新初始化 customRange
        // 不要复用残留的 customRange——人脑预期是"自定义起点 = 当前看到的窗口"，而非历史残留
        if (newPreset === 'CUSTOM' && value.preset !== 'CUSTOM') {
            const now = dayjs();
            const hours = presetToHours(value.preset);
            onChange({
                ...value,
                preset: newPreset,
                customRange: [now.subtract(hours, 'hour'), now]
            });
            return;
        }
        onChange({ ...value, preset: newPreset });
    };

    const isPresetRange = value.preset === 'LAST_24H' || value.preset === 'LAST_48H';

    // 计算 RangePicker 显示值:自定义用用户选的,预设用基于 now 的滚动窗口
    const displayRange: [Dayjs, Dayjs] | null = useMemo(() => {
        if (value.preset === 'CUSTOM') return value.customRange ?? null;
        if (isPresetRange) {
            const now = dayjs();
            return [now.subtract(presetToHours(value.preset), 'hour'), now];
        }
        return null;
    }, [value.preset, value.customRange, isPresetRange]);

    return (
        <div className="flex items-center gap-3 flex-wrap">
            <Radio.Group
                value={value.preset}
                onChange={(e) => handlePresetChange(e.target.value)}
                size="middle"
            >
                <Radio.Button value="LAST_24H">{t(`${I18N}.last24h`)}</Radio.Button>
                <Radio.Button value="LAST_48H">{t(`${I18N}.last48h`)}</Radio.Button>
                <Radio.Button value="CUSTOM">{t(`${I18N}.custom`)}</Radio.Button>
            </Radio.Group>
            <DatePicker.RangePicker
                disabled={isPresetRange}
                showTime={{ format: 'HH', showMinute: false, showSecond: false }}
                format="YYYY-MM-DD HH"
                value={displayRange ?? undefined}
                onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                        const diffInDays = dates[1].diff(dates[0], 'day', true);
                        if (diffInDays > INHUB_MAX_SPAN_DAYS) {
                            message.warning(t(`${I18N}.maxRange`, { days: INHUB_MAX_SPAN_DAYS }));
                            return;
                        }
                        onChange({ ...value, customRange: [dates[0], dates[1]] });
                    }
                }}
                allowClear={false}
                size="middle"
            />
        </div>
    );
};

export default TimeRangeFilter;
